import {Alert, FormControl} from 'platform/components';
import {UseDocumentPreviewReturnType} from 'platform/document-preview';
import {Heading, HStack, Icon, VStack, Text, Show, Hide} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import i18n from '@dms/i18n';

import {DOT_CHARACTER, useDebouncedValue} from 'shared/web';

import {DelayEnum} from '../constants/DelayEnum';
import {useInvalidItemsCount} from '../hooks/useInvalidItemsCount';
import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';

interface ListOfItemsHeaderProps {
  isConfirmed: boolean;
  isLoadingSuggestions: boolean;
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>;
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  pageNumber: number;
  hasMoreThanOnePage: boolean;
  documentPreview: UseDocumentPreviewReturnType;
}

export function ListOfItemsHeader(props: ListOfItemsHeaderProps) {
  const listOfItems = useWatch({control: props.control, name: 'listOfItems'});

  const debouncedListOfItems = useDebouncedValue(listOfItems, DelayEnum.HEADER);

  const {invalidItemsCount, invalidItemsCountPerPage} = useInvalidItemsCount({
    isConfirmed: props.isConfirmed,
    pageNumber: props.pageNumber,
    listOfItems: debouncedListOfItems,
    documentPreview: props.documentPreview,
  });

  const hasActiveItems = debouncedListOfItems.some((item) => item.isActive);

  const hasInvalidItems = invalidItemsCount > 0;

  const hasInvalidItemsOnCurrentPage = invalidItemsCountPerPage > 0;

  return (
    <VStack spacing={4}>
      <HStack justify="space-between">
        <Heading size={4}>{i18n.t('entity.warehouse.labels.listOfItems')}</Heading>

        <Hide when={!props.isConfirmed}>
          <Hide when={!hasActiveItems}>
            <HStack spacing={1}>
              <HStack spacing={1}>
                <Icon
                  value={hasInvalidItems ? 'alert/error' : 'action/check_circle'}
                  color={hasInvalidItems ? 'severity.danger' : 'severity.success'}
                  size={4}
                />
                <Text size="xSmall">{i18n.t('entity.documentExtraction.labels.allPages')}</Text>
                <Text size="xSmall" color="tertiary">
                  {DOT_CHARACTER}
                </Text>
              </HStack>
              <Text size="xSmall">
                {i18n.t('entity.documentExtraction.labels.issues', {count: invalidItemsCount})}
              </Text>
            </HStack>
          </Hide>
        </Hide>
      </HStack>

      <Show when={props.isLoadingSuggestions}>
        <Alert
          isLoading
          layout="detailed"
          type="inline"
          variant="info"
          title={i18n.t('entity.documentExtraction.labels.checkingArticleCards')}
        />
      </Show>

      <Hide when={props.isLoadingSuggestions}>
        <Show when={!props.isConfirmed}>
          <Alert
            type="inline"
            variant="info"
            title={i18n.t('entity.documentExtraction.labels.itemsAreNotAvailable')}
            message={i18n.t('entity.documentExtraction.labels.itemsAreNotAvailableMessage')}
          />
        </Show>

        <Hide when={!props.isConfirmed || !hasActiveItems}>
          <Alert
            type="inline"
            variant={hasInvalidItemsOnCurrentPage ? 'error' : 'success'}
            title={
              hasInvalidItemsOnCurrentPage
                ? i18n.t('entity.documentExtraction.labels.invalidPageAlert', {
                    pageNumber: props.pageNumber,
                    count: invalidItemsCountPerPage,
                  })
                : i18n.t('entity.documentExtraction.labels.successPageAlert', {
                    pageNumber: props.pageNumber,
                  })
            }
            message={
              props.hasMoreThanOnePage && hasInvalidItems && !hasInvalidItemsOnCurrentPage
                ? i18n.t('entity.documentExtraction.labels.successPageAlertMessage')
                : undefined
            }
          />
        </Hide>

        <Show when={!hasActiveItems}>
          <Alert
            type="inline"
            variant="warning"
            title={i18n.t('entity.documentExtraction.labels.noItemsActiveAlert')}
            message={i18n.t('entity.documentExtraction.labels.noItemsActiveAlertMessage')}
          />
        </Show>
      </Hide>
    </VStack>
  );
}
